import React, { useState } from "react";
import { Tabs, Table, Space, Tag, Modal, Form, Input, Select, Switch, Tooltip, Typography, Empty, AutoComplete } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrashCan, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { ProhibitedArea } from "components";

const { Option } = Select;
const { Text, Paragraph } = Typography;

const AUTH_LABEL = { api_key: "API key", iam: "IAM", optional: "Optional key" };

function AiModelRouting({
  catalog = { providers: [], capabilities: [] },
  providers = [],
  bindings = [],
  permission,
  providerModels = {},
  providerModelsLoading = {},
  providerModelsSource = {},
  onFetchModels,
  onSaveProvider,
  onRemoveProvider,
  onSaveBinding,
  onRemoveBinding,
}) {
  const [providerModal, setProviderModal] = useState({ open: false, entry: null });
  const [bindingModal, setBindingModal] = useState({ open: false, capability: null });
  const [providerForm] = Form.useForm();
  const [bindingForm] = Form.useForm();
  const [selectedBindingProvider, setSelectedBindingProvider] = useState(null);
  const [capabilitySearch, setCapabilitySearch] = useState("");

  if (!permission) {
    return null; 
  }
  if (!permission.canView) {
    return <ProhibitedArea />;
  }

  const configByProvider = (name) => providers.find((p) => p.provider === name);
  const bindingByCapability = (cap) => bindings.find((b) => b.capability === cap);
  const providerLabel = (name) => (catalog.providers.find((p) => p.provider === name) || {}).label || name;
  const defaultProviderCfg = providers.find((p) => p.isDefault && p.enabled);

  const modelOptions = (provider) =>
    ((providerModels && providerModels[provider]) || []).map((m) => ({ value: m.id, label: m.label || m.id }));
  const modelsLoading = (provider) => !!(providerModelsLoading && providerModelsLoading[provider]);
  const fetchModels = (provider) => {
    if (provider && onFetchModels) onFetchModels(provider);
  };
  const modelFilter = (input, option) =>
    ((option && option.value) || "").toLowerCase().includes((input || "").toLowerCase());
  const modelPlaceholder = (provider) =>
    provider === "AZURE_OPENAI"
      ? "Enter the Azure deployment name"
      : modelsLoading(provider)
        ? "Loading models…"
        : "Select or type a model id";
  const modelHelp = (provider, exampleModels) => {
    if (!provider) return undefined;
    if (modelsLoading(provider)) return "Loading models…";
    const src = providerModelsSource && providerModelsSource[provider];
    const count = ((providerModels && providerModels[provider]) || []).length;
    switch (src) {
      case "freetext":
        return "Azure routes by deployment name, which can't be listed — type your deployment name.";
      case "no-key":
        return "No models to list yet — add this provider's key under System Keys, or type a model id.";
      case "no-endpoint":
        return "Set the provider's Base URL first to list models, or type a model id.";
      case "error":
        return "Couldn't load models from the provider — type a model id.";
      case "live":
        return count > 0 ? `${count} live models from the provider.` : "No models returned — type a model id.";
      default:
        return exampleModels && exampleModels.length ? `e.g. ${exampleModels.join(", ")}` : undefined;
    }
  };

  const openProviderModal = (entry) => {
    const cfg = configByProvider(entry.provider) || {};
    providerForm.resetFields();
    providerForm.setFieldsValue({
      enabled: cfg.enabled !== undefined ? cfg.enabled : true,
      defaultModel: cfg.defaultModel,
      isDefault: cfg.isDefault || false,
      settings: cfg.settings || {},
    });
    fetchModels(entry.provider);
    setProviderModal({ open: true, entry });
  };

  const submitProvider = async () => {
    const values = await providerForm.validateFields();
    onSaveProvider(providerModal.entry.provider, {
      enabled: values.enabled,
      defaultModel: values.defaultModel,
      isDefault: values.isDefault || false,
      settings: values.settings || {},
    });
    setProviderModal({ open: false, entry: null });
  };

  const providerColumns = [
    {
      title: "Provider",
      dataIndex: "label",
      render: (label, entry) => (
        <div>
          <Text strong>{label}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{entry.description}</Text></div>
        </div>
      ),
    },
    { title: "Auth", dataIndex: "authMode", width: 110, render: (m) => <Tag>{AUTH_LABEL[m] || m}</Tag> },
    {
      title: "Status",
      width: 180,
      render: (_t, entry) => {
        const cfg = configByProvider(entry.provider);
        if (!cfg) return <Tag>Not configured</Tag>;
        return (
          <Space size={4}>
            {cfg.enabled ? <Tag color="green">Enabled</Tag> : <Tag color="red">Disabled</Tag>}
            {cfg.isDefault && <Tag color="blue">Default</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Default model",
      width: 200,
      render: (_t, entry) => {
        const cfg = configByProvider(entry.provider);
        return <Text>{(cfg && cfg.defaultModel) || "—"}</Text>;
      },
    },
    {
      title: "",
      width: 90,
      render: (_t, entry) => {
        const cfg = configByProvider(entry.provider);
        return (
          <Space>
            <Tooltip title="Configure">
              <FontAwesomeIcon
                icon={faPencil}
                className={permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                onClick={() => (permission.canEdit ? openProviderModal(entry) : null)}
              />
            </Tooltip>
            {cfg && (
              <Tooltip title="Remove configuration">
                <FontAwesomeIcon
                  icon={faTrashCan}
                  className={permission.canDelete ? "action-cursor text-red-500" : "text-gray-400 opacity-45"}
                  onClick={() => (permission.canDelete ? onRemoveProvider(entry.provider) : null)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const openBindingModal = (capability) => {
    const b = bindingByCapability(capability) || {};
    bindingForm.resetFields();
    bindingForm.setFieldsValue({ provider: b.provider, model: b.model });
    setSelectedBindingProvider(b.provider || null);
    if (b.provider) fetchModels(b.provider);
    setBindingModal({ open: true, capability });
  };

  const submitBinding = async () => {
    const values = await bindingForm.validateFields();
    onSaveBinding(bindingModal.capability, { provider: values.provider, model: values.model });
    setBindingModal({ open: false, capability: null });
  };

  const bindingColumns = [
    {
      title: "Capability",
      dataIndex: "capability",
      render: (cap, entry) => (
        <div>
          <Text strong>{cap}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{entry.description}</Text></div>
        </div>
      ),
    },
    {
      title: "Routes to",
      width: 320,
      render: (_t, entry) => {
        const b = bindingByCapability(entry.capability);
        if (b) {
          return <Text>{providerLabel(b.provider)} <Text type="secondary">/ {b.model}</Text></Text>;
        }
        if (defaultProviderCfg) {
          return (
            <Text>
              {providerLabel(defaultProviderCfg.provider)} <Text type="secondary">/ {defaultProviderCfg.defaultModel}</Text>{" "}
              <Tag color="blue" style={{ marginLeft: 4 }}>tenant default</Tag>
            </Text>
          );
        }
        return <Text type="secondary">Platform default</Text>;
      },
    },
    {
      title: "",
      width: 90,
      render: (_t, entry) => {
        const b = bindingByCapability(entry.capability);
        return (
          <Space>
            <Tooltip title="Set model">
              <FontAwesomeIcon
                icon={faPencil}
                className={permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                onClick={() => (permission.canEdit ? openBindingModal(entry.capability) : null)}
              />
            </Tooltip>
            {b && (
              <Tooltip title="Reset to default">
                <FontAwesomeIcon
                  icon={faTrashCan}
                  className={permission.canDelete ? "action-cursor text-red-500" : "text-gray-400 opacity-45"}
                  onClick={() => (permission.canDelete ? onRemoveBinding(entry.capability) : null)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const modalEntry = providerModal.entry || {};
  const bindingProviderMeta = catalog.providers.find((p) => p.provider === selectedBindingProvider);

  const capabilityQuery = capabilitySearch.trim().toLowerCase();
  const filteredCapabilities = capabilityQuery
    ? (catalog.capabilities || []).filter(
        (c) =>
          (c.capability || "").toLowerCase().includes(capabilityQuery) ||
          (c.description || "").toLowerCase().includes(capabilityQuery)
      )
    : catalog.capabilities;

  const tabHint = (text) => (
    <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 12 }}>
      {text}
    </Text>
  );

  const tabItems = [
    {
      key: "providers",
      label: "Providers",
      children: (
        <>
          {tabHint(
            <>
              Set each provider&apos;s connection and default model here. API keys are managed
              under <Text strong>System Keys</Text>.
            </>
          )}
          <Table
            rowKey="provider"
            columns={providerColumns}
            dataSource={catalog.providers}
            pagination={false}
            locale={{ emptyText: <Empty description="No providers available" /> }}
          />
        </>
      ),
    },
    {
      key: "bindings",
      label: "Model Routing",
      children: (
        <>
          {tabHint("Route each capability to a provider and model. Capabilities left unset use the platform default.")}
          <Input
            allowClear
            prefix={<FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400" />}
            placeholder="Search capabilities by name or description"
            value={capabilitySearch}
            onChange={(e) => setCapabilitySearch(e.target.value)}
            style={{ maxWidth: 360, marginBottom: 12 }}
          />
          <Table
            rowKey="capability"
            columns={bindingColumns}
            dataSource={filteredCapabilities}
            pagination={false}
            sticky
            scroll={{ y: "calc(100vh - 360px)" }}
            locale={{
              emptyText: (
                <Empty
                  description={capabilityQuery ? `No capabilities match "${capabilitySearch}"` : "No capabilities defined"}
                />
              ),
            }}
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <Tabs items={tabItems} />

      <Modal
        title={`Configure ${modalEntry.label || ""}`}
        open={providerModal.open}
        onOk={submitProvider}
        okText="Save"
        okButtonProps={{ disabled: !(permission.canAdd || permission.canEdit) }}
        onCancel={() => setProviderModal({ open: false, entry: null })}
        destroyOnClose
      >
        {modalEntry.description && <Paragraph type="secondary">{modalEntry.description}</Paragraph>}
        {modalEntry.requiresApiKey && (
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            This provider needs an API key — add it under <Text strong>System Keys</Text> (type{" "}
            <Text code>{modalEntry.keyType}</Text>).
          </Paragraph>
        )}
        {modalEntry.authMode === "iam" && (
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Authenticates via the platform IAM role — no key required.
          </Paragraph>
        )}
        <Form form={providerForm} layout="vertical" name="providerConfig">
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Default model" name="defaultModel"
            help={modelHelp(modalEntry.provider, modalEntry.exampleModels)}>
            <AutoComplete
              options={modelOptions(modalEntry.provider)}
              filterOption={modelFilter}
              allowClear
              notFoundContent={modelsLoading(modalEntry.provider) ? "Loading…" : "No models found — type an id"}
              placeholder={modelPlaceholder(modalEntry.provider)}
            />
          </Form.Item>
          <Form.Item
            label="Use as tenant default"
            name="isDefault"
            valuePropName="checked"
            extra="Route every capability that has no specific model to this provider (using the default model above). You can still override individual capabilities under Model Routing."
          >
            <Switch />
          </Form.Item>
          {(modalEntry.connectionFields || []).map((field) => (
            <Form.Item
              key={field.name}
              label={field.label}
              name={["settings", field.name]}
              help={field.help}
              rules={field.required ? [{ required: true, message: `${field.label} is required` }] : []}
            >
              {field.type === "secret" ? (
                <Input.Password placeholder={field.placeholder} />
              ) : (
                <Input placeholder={field.placeholder} />
              )}
            </Form.Item>
          ))}
        </Form>
      </Modal>

      <Modal
        title={`Route "${bindingModal.capability || ""}"`}
        open={bindingModal.open}
        onOk={submitBinding}
        okText="Save"
        okButtonProps={{ disabled: !(permission.canAdd || permission.canEdit) }}
        onCancel={() => setBindingModal({ open: false, capability: null })}
        destroyOnClose
      >
        <Form form={bindingForm} layout="vertical" name="modelBinding">
          <Form.Item label="Provider" name="provider" rules={[{ required: true, message: "Select a provider" }]}>
            <Select
              placeholder="Select a provider"
              onChange={(v) => {
                setSelectedBindingProvider(v);
                bindingForm.setFieldsValue({ model: undefined });
                fetchModels(v);
              }}
            >
              {catalog.providers.map((p) => (
                <Option key={p.provider} value={p.provider}>{p.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Model" name="model" rules={[{ required: true, message: "Enter a model id" }]}
            help={modelHelp(selectedBindingProvider, bindingProviderMeta && bindingProviderMeta.exampleModels)}>
            <AutoComplete
              options={modelOptions(selectedBindingProvider)}
              filterOption={modelFilter}
              allowClear
              disabled={!selectedBindingProvider}
              notFoundContent={modelsLoading(selectedBindingProvider) ? "Loading…" : "No models found — type an id"}
              placeholder={selectedBindingProvider ? modelPlaceholder(selectedBindingProvider) : "Select a provider first"}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AiModelRouting;
